import React from 'react';
import debounce from 'lodash-es/debounce';

import RecommendKeyword from '~/components/RecommendKeyword';
import { fetchCurrentUser } from '~/domains/auth/queries';
import { useTrackClick } from '~/hooks/event';
import { MobileAppContextProvider } from '~/hooks/mobileApp';
import { useOAIQuery } from '~/hooks/reactQuery';
import { useUpdateRouterQuery } from '~/hooks/url';
import { useBusinessIdStore } from '~/store/global/useBusinessIdStore';

import AppMarketHomeLink from './AppMarketHomeLink';
import SearchLayout from './SearchLayout';

export type CustomSearchEvent = CustomEvent<{ value: string }>;

const RecommendSearchLayout: React.FC<{
  children: React.ReactNode;
  placeholder?: string;
}> = ({ children, placeholder = '상품명을 검색해주세요' }) => {
  const [value, setValue] = React.useState('');
  const [focusing, setFocusing] = React.useState(true);

  const { current: updateValue } = React.useRef(debounce(setValue, 400));
  const { businessId } = useBusinessIdStore();

  const updateRouterQuery = useUpdateRouterQuery();
  const trackClick = useTrackClick();

  const showRecommendKeywordsField = Boolean(value && focusing);

  const { data = [] } = useOAIQuery({
    queryKey: '/api/market-place/v1/search/auto-completion',
    variables: { keyword: value, size: 10 },
    queryOptions: {
      enabled: showRecommendKeywordsField,
      suspense: false,
      keepPreviousData: true,
    },
  });

  const handleChange = (v: string) => {
    if (!focusing) {
      setFocusing(true);
    }
    updateValue(v);
  };

  const handleKeywordClick = async (keyword: string) => {
    const user = await fetchCurrentUser();
    trackClick({
      type: 'button',
      name: '추천 검색어',
      properties: {
        keyword,
        userId: user.id,
        businessId,
      },
    });

    await updateRouterQuery({ searchText: keyword });
  };

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement, Element>) => {
    const relatedTarget = e.relatedTarget as HTMLElement;

    if (relatedTarget && relatedTarget.id.includes('keyword')) {
      // 라우팅 처리 이 후 포커스 false 처리
      await handleKeywordClick(relatedTarget.dataset.keyword as string);
    }
    setFocusing(false);
  };

  const handleFocus = () => {
    setFocusing(true);
  };

  return (
    <MobileAppContextProvider LinkComponent={AppMarketHomeLink}>
      <SearchLayout
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
      >
        {showRecommendKeywordsField ? (
          <div className="px-20">
            {data.map((d) => (
              <RecommendKeyword
                key={d.suggestedKeyword}
                keyword={d.suggestedKeyword}
                query={value}
              />
            ))}
          </div>
        ) : (
          children
        )}
      </SearchLayout>
    </MobileAppContextProvider>
  );
};

export default RecommendSearchLayout;
